import { Link } from "react-router-dom";
import { Mail, Globe, Instagram, Facebook, Phone, MessageCircle } from "lucide-react";

const LOGO = "https://customer-assets.emergentagent.com/job_ai-legal-test/artifacts/trhkf0y7_justice%20ai%20logo.png";
const PRIVACY_PDF = "https://customer-assets.emergentagent.com/job_ai-legal-test/artifacts/t6b1g2n8_PRIVACY%20POLICY%20-%20JUSTICE%20AI.pdf";
const TERMS_PDF = "https://customer-assets.emergentagent.com/job_ai-legal-test/artifacts/4ehfyx0m_TERMS%20OF%20SERVICE%20-JUSTICE-AI.pdf";

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const footerLinks = {
    Platform: [
      { href: "/get-legal-help", label: "For Citizens" },
      { href: "/for-lawyers", label: "For Lawyers" },
      { href: "/for-ngos", label: "Justice Wallet" },
      { href: "/for-governments", label: "Institutional" },
    ],
    Resources: [
      { href: "/how-it-works", label: "How It Works" },
      { href: "/ai-assistant", label: "Justice-AI Assistant" },
      { href: "/about", label: "About Us" },
      { href: "/contact", label: "Contact" },
    ],
    Legal: [
      { href: PRIVACY_PDF, label: "Privacy Policy", external: true },
      { href: TERMS_PDF, label: "Terms of Service", external: true },
    ],
  };

  return (
    <footer className="relative bg-ink text-white border-t border-white/10" data-testid="footer">
      <div className="absolute inset-x-0 top-0 h-px divider-hairline" />
      <div className="container mx-auto px-6 max-w-7xl">
        <div className="py-16 lg:py-20">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-12">
            {/* Brand block */}
            <div className="lg:col-span-5">
              <Link to="/" className="flex items-center gap-3 mb-5" data-testid="footer-logo">
                <img src={LOGO} alt="AccessJustice.Global" className="h-12 w-auto" />
                <div className="leading-none">
                  <div className="font-playfair text-2xl text-white">
                    Access<span className="italic text-turquoise">Justice</span>
                    <span className="text-gold-300">.</span>Global
                  </div>
                  <div className="font-montserrat text-[9px] tracking-[0.32em] text-gold-200/80 uppercase mt-1.5">
                    The Operating System for Justice
                  </div>
                </div>
              </Link>
              <p className="font-inter text-sm leading-relaxed text-white/55 max-w-md mb-6">
                AccessJustice.Global is an AI-powered digital justice platform connecting
                people who need legal help with verified lawyers, intelligent legal
                guidance, and funded access to justice through the Justice Wallet.
              </p>
              <div className="space-y-2.5 text-sm text-white/60 font-inter">
                <a href="mailto:info@accessjusticeglobal.org" className="flex items-center gap-2.5 hover:text-turquoise transition-colors group">
                  <Mail className="w-4 h-4 text-turquoise" />
                  info@accessjusticeglobal.org
                </a>
                <a href="https://wa.me/2348114445557" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2.5 hover:text-turquoise transition-colors">
                  <MessageCircle className="w-4 h-4 text-turquoise" />
                  +234 811 444 5557 (WhatsApp)
                </a>
                <div className="flex items-center gap-2.5">
                  <Globe className="w-4 h-4 text-turquoise" />
                  Global Operations · Built in Nigeria
                </div>
              </div>
            </div>

            {/* Link columns */}
            <div className="lg:col-span-7 grid grid-cols-2 md:grid-cols-3 gap-8">
              {Object.entries(footerLinks).map(([title, links]) => (
                <div key={title}>
                  <h4 className="font-montserrat text-xs uppercase tracking-[0.22em] text-gold-200 mb-5">
                    {title}
                  </h4>
                  <ul className="space-y-3">
                    {links.map((link) => (
                      <li key={link.href}>
                        {link.external ? (
                          <a
                            href={link.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-inter text-sm text-white/65 hover:text-turquoise transition-colors"
                            data-testid={`footer-ext-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                          >
                            {link.label}
                          </a>
                        ) : (
                          <Link
                            to={link.href}
                            className="font-inter text-sm text-white/65 hover:text-turquoise transition-colors"
                            data-testid={`footer-link-${link.href.slice(1)}`}
                          >
                            {link.label}
                          </Link>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-white/10 py-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="font-inter text-xs text-white/45 tracking-wide">
            © {currentYear} AccessJustice.Global · All rights reserved
          </p>
          <div className="flex items-center gap-5">
            <a
              href="https://www.instagram.com/accessjustice.global/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram"
              data-testid="footer-instagram"
              className="text-white/55 hover:text-turquoise transition-colors"
            >
              <Instagram className="w-4.5 h-4.5" />
            </a>
            <a
              href="https://www.facebook.com/profile.php?id=61577465846664"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Facebook"
              data-testid="footer-facebook"
              className="text-white/55 hover:text-turquoise transition-colors"
            >
              <Facebook className="w-4.5 h-4.5" />
            </a>
            <a
              href="https://wa.me/2348114445557"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="WhatsApp"
              data-testid="footer-whatsapp"
              className="text-white/55 hover:text-turquoise transition-colors"
            >
              <Phone className="w-4.5 h-4.5" />
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
